import {
	type LoaderFunctionArgs,
	type MetaFunction,
	type HeadersArgs,
	data,
	useLoaderData,
	useNavigate,
} from 'react-router'
import { isbot } from 'isbot'
import { useTranslation } from 'react-i18next'
import { LoadMoreButton } from '~/components/button/load-more-button'
import Container from '~/components/container/container'
import EventList from '~/components/event-list/event-list'
import { Icon } from '~/components/icon/icon'
import { IndexSection } from '~/components/index-section/index-section'
import { Spacer } from '~/components/spacer/spacer'
import EventListSwiper from '~/components/swiper/event-list-swiper'
import Text from '~/components/text/text'
import { getIndexEvents } from '~/queries/indexEvents/getIndexEvents.server'
import { getPromotedIndexEvents } from '~/queries/indexEvents/getPromotedIndexEvents.server'
import { getLanguageFromCountryCode, getServerLang } from '~/utils/i18n.utils'
import { getUserLocation } from '~/utils/location/location.server'
import { getUrlQueries } from '~/utils/queries/getUrlQueries.server'
import { SetMeta } from '~/utils/seo/seo'
import { makeTimings, time } from '~/utils/timing.server'
import i18next from '~/configs/i18next.server'
import { getPlace } from '~/utils/location/location'
import { Button } from '~/components/button/button'

export async function loader({ params, request }: LoaderFunctionArgs) {
	const timings = makeTimings('s loader')
	const userLocation = await getUserLocation(request, params)
	const {
		countryCode,
		city,
		swLat,
		swLng,
		neLat,
		neLng,
		radius,
		latitude,
		longitude,
		nearbySelected,
		havePosition,
	} = userLocation
	const { fromDate, toDate, categories, page, searchText } =
		await getUrlQueries(request)
	const isBot = isbot(request.headers.get('user-agent'))
	const pageSize = 20
	const lng = getServerLang(request)
	const t = await i18next.getFixedT(lng.lngShort, 'common')
	const place = getPlace(userLocation, t, true)

	const promotedEvents = await time(
		async () =>
			await getPromotedIndexEvents({
				fromDate: fromDate,
				toDate: toDate,
				city: city,
				countryCode: havePosition && nearbySelected ? undefined : countryCode,
				radius: havePosition && nearbySelected ? radius : undefined,
				latitude: havePosition && nearbySelected ? latitude : undefined,
				longitude: havePosition && nearbySelected ? longitude : undefined,
				swLat: swLat,
				swLng: swLng,
				neLat: neLat,
				neLng: neLng,
				categories: categories,
				take: 9,
				isBot: isBot,
				searchText: searchText,
				lng: lng,
			}),
		{ timings, type: 'get promoted events' },
	)

	const events = await time(
		async () =>
			await getIndexEvents({
				fromDate: fromDate,
				toDate: toDate,
				city: city,
				countryCode: havePosition && nearbySelected ? undefined : countryCode,
				radius: havePosition && nearbySelected ? radius : undefined,
				latitude: havePosition && nearbySelected ? latitude : undefined,
				longitude: havePosition && nearbySelected ? longitude : undefined,
				swLat: swLat,
				swLng: swLng,
				neLat: neLat,
				neLng: neLng,
				categories: categories,
				take: pageSize * page,
				searchText: searchText,
				lng: lng,
			}),
		{ timings, type: 'get events' },
	)

	return data(
		{
			events,
			promotedEvents,
			page,
			pageSize,
			userLocation,
			translations: {
				title: t('public_all_events'),
				description: t('public_evently_slogan_description'),
				nearYou: t('public_events_near_you'),
				place,
				publicPlaceTitle: t('public_events_in', { location: place }),
				publicPlaceDescription: t('public_events_in_description', {
					location: place,
				}),
				allEventsNearYou: t('public_all_events_near_you'),
				findAllEvents: t('public_find_all_events'),
				welcomeToEvently: t('public_welcome_to_evently'),
			},
		},
		{ headers: { 'Server-Timing': timings.toString() } },
	)
}

export function headers({ parentHeaders, loaderHeaders }: HeadersArgs) {
	parentHeaders.append('Server-Timing', loaderHeaders.get('Server-Timing')!)
	return parentHeaders
}

export default function Index() {
	const { events, promotedEvents, page, pageSize } =
		useLoaderData<typeof loader>()
	const { t } = useTranslation()
	const navigate = useNavigate()

	function clearQueries() {
		navigate(`${window.location.pathname}`, {
			replace: true,
		})
	}

	return (
		<>
			<Container marginY={false}>
				{promotedEvents.length >= 3 && (
					<IndexSection>
						<EventListSwiper events={promotedEvents} type="promoted" />
					</IndexSection>
				)}
				{events.length > 0 && (
					<IndexSection>
						<EventList
							events={events}
							showFavorite
							aspectRatio="video"
							size="md"
						/>
					</IndexSection>
				)}
				{events.length <= 0 && (
					<section className="mt-12 flex h-full justify-center text-center align-middle">
						<div className="max-w-2xl">
							<Icon name="face-down" size="xxxxl" className="drop-shadow-md" />
							<Text as="h2" className="my-4 md:text-3xl!">
								{t('error_no_events_in_search')}
							</Text>
							<Text className="my-6 md:text-lg!">
								{t('action_try_again_or_clear_search')}
							</Text>
							<Spacer size="4xs" />
							<Button onClick={clearQueries}>{t('action_clear_search')}</Button>
							<Spacer size="md" />
						</div>
					</section>
				)}

				<LoadMoreButton
					page={page}
					pageSize={pageSize}
					count={events.length}
					text={t('action_load_more_events')}
					loadingText={t('events_loading_events')}
				/>
			</Container>
		</>
	)
}

export const meta: MetaFunction<typeof loader> = ({ data, location }) => {
	return SetMeta({
		title:
			data?.userLocation.havePosition && data.userLocation.nearbySelected
				? data.translations.nearYou
				: data?.translations.place
					? data.translations.publicPlaceTitle
					: data?.translations.title,
		description: data?.translations.place
			? data.translations.publicPlaceDescription
			: data?.userLocation.havePosition && data.userLocation.nearbySelected
				? data.translations.allEventsNearYou
				: data?.translations.findAllEvents +
					' ' +
					data?.translations.welcomeToEvently,
		location: location,
		canonical: getLanguageFromCountryCode(data?.userLocation?.countryCode),
		country: data?.userLocation?.countryCode,
	})
}
